/**
 * Разбор ошибок Laravel-бэка: тело приходит как { message?, errors? }, где errors —
 * это { поле: [сообщения] }, а при падении на уникальном индексе вместо полей
 * приезжает errors.exception с сырым SQL («Duplicate entry … for key 'users_email_unique'»).
 *
 * Собрано из уже работающего разбора в карточке студента (app/groups/.../StudentDrawerContent),
 * чтобы «почта занята» показывалась одинаково во всех формах.
 */

export type ErrorBag = Record<string, unknown>;

/** Поля, про которые умеем сказать человеческим языком. */
export const KNOWN_ERROR_FIELDS = [
  "email",
  "phone",
  "username",
  "firstname",
  "lastname",
  "password",
  "invite_code",
] as const;

export type KnownErrorField = (typeof KNOWN_ERROR_FIELDS)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/**
 * Коды из старого фронта (oqylyq-nuxt, handleErrors на страницах регистрации) —
 * бэк отдаёт их в теле вместе с 400, и только по ним понятно, что именно занято.
 */
export const API_STATUS_CODE = {
  emailExists: 1101,
  phoneExists: 9101,
  phoneNotFound: 9102,
  wrongVerificationCode: 9103,
} as const;

/**
 * Код как он пришёл: часть состояний бэка объявлена строками ('0107', '0103'),
 * и числовой getStatusCode их не видит.
 */
export function getStatusCodeRaw(error: unknown): string | number | null {
  if (!isRecord(error)) return null;

  const data = error.data;
  if (isRecord(data) && (typeof data.status_code === "string" || typeof data.status_code === "number")) {
    return data.status_code;
  }

  if (typeof error.status_code === "string" || typeof error.status_code === "number") {
    return error.status_code;
  }

  return null;
}

export function getStatusCode(error: unknown): number | null {
  if (!isRecord(error)) return null;

  const data = error.data;
  if (isRecord(data) && typeof data.status_code === "number") return data.status_code;
  if (typeof error.status_code === "number") return error.status_code;

  return null;
}

/** HTTP-статус ответа (RTK Query кладёт его в error.status). */
export function getHttpStatus(error: unknown): number | null {
  if (!isRecord(error)) return null;
  return typeof error.status === "number" ? error.status : null;
}

export function getErrorBag(error: unknown): ErrorBag | null {
  if (!isRecord(error)) return null;

  const data = error.data;
  if (isRecord(data) && isRecord(data.errors)) return data.errors as ErrorBag;
  if (isRecord(error.errors)) return error.errors as ErrorBag;

  return null;
}

/** Текст message/exception — на случай, когда полей в ошибке нет. */
export function getErrorText(error: unknown): string | null {
  if (typeof error === "string") return error;
  if (!isRecord(error)) return null;

  const data = error.data;

  if (typeof data === "string" && data.trim()) return data.trim();

  if (isRecord(data)) {
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
    if (isRecord(data.error) && typeof data.error.message === "string") return data.error.message;
    if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
  }

  if (typeof error.message === "string" && error.message.trim()) return error.message.trim();

  return null;
}

const messagesOf = (bag: ErrorBag, field: string): string[] => {
  const value = bag[field];
  if (Array.isArray(value)) return value.filter((m): m is string => typeof m === "string");
  return typeof value === "string" ? [value] : [];
};

const exceptionTextOf = (bag: ErrorBag): string =>
  typeof bag.exception === "string" ? bag.exception.toLowerCase() : "";

/** Поля, которые бэк считает уже занятыми: и по тексту валидации, и по SQL-дублю. */
export function takenFields(bag: ErrorBag): KnownErrorField[] {
  const exception = exceptionTextOf(bag);
  const isUniqueException =
    exception.includes("1062") ||
    exception.includes("duplicate entry") ||
    exception.includes("unique");

  return KNOWN_ERROR_FIELDS.filter((field) => {
    const byValidation = [field, `user.${field}`].some((key) =>
      messagesOf(bag, key).some((m) => {
        const text = m.toLowerCase();
        return text.includes("already been taken") || text.includes("уже зан");
      }),
    );

    const byException =
      isUniqueException &&
      [`_${field}_`, `.${field}`, `'${field}'`, `\`${field}\``, ` ${field} `].some((mark) =>
        exception.includes(mark),
      );

    return byValidation || byException;
  });
}

/** Поля, которые бэк забраковал по любой другой причине. */
export function invalidFields(bag: ErrorBag): KnownErrorField[] {
  const taken = new Set(takenFields(bag));

  return KNOWN_ERROR_FIELDS.filter(
    (field) =>
      !taken.has(field) &&
      [field, `user.${field}`].some((key) => messagesOf(bag, key).length > 0),
  );
}

/** Первое сообщение самого бэка — если оно на понятном языке, лучше показать его. */
export function firstFieldMessage(bag: ErrorBag): string | null {
  for (const key of Object.keys(bag)) {
    if (key === "exception") continue;
    const [message] = messagesOf(bag, key);
    if (message) return message;
  }

  return null;
}
